import api from '../service/api.service';

const BASE_URL = '/v1/fo/audit';

export type ActivityActorType =
  | 'ADMIN'
  | 'FIRM_USER'
  | 'FIRM_SERVICE_USER'
  | 'CUSTOMER'
  | 'INTEGRATION'
  | 'SYSTEM'
  | 'UNKNOWN'
  // A firm's vendor, through the confirmation link emailed to it
  | 'VENDOR';

export type ActivityOutcome = 'SUCCESS' | 'FAILURE' | 'DENIED';

export type ActivityOperation = 'insert' | 'update' | 'delete' | 'truncate';

/** What kind of thing happened. */
export type ActivityCategory =
  | 'AUTH'
  | 'MUTATION'
  | 'TRANSACTION'
  | 'ADMIN'
  | 'DOCUMENT'
  | 'EXPORT'
  | 'NAVIGATION'
  | 'SYSTEM';

export interface ActivityFieldChange {
  field: string;
  /** Readable name of a key inside a JSON column (e.g. "Payment 2 · Payment intention"). */
  fieldLabel?: string | null;
  policy: string;
  /** As stored under the column policy: masked (e.g. ****1234), or null when the value is not stored. */
  oldValue: string | null;
  newValue: string | null;
  /** Readable name of a stored code (e.g. "Active" for "A"), when there is one. */
  oldLabel: string | null;
  newLabel: string | null;
  isSensitive: boolean;
}

export interface ActivityMatter {
  caseId: number;
  referenceId: string;
}

export interface ActivityEvent {
  id: string;
  occurredAt: string;
  action: string;
  outcome: ActivityOutcome;
  category: ActivityCategory;
  area: string | null;
  /** The database operation; null for events such as page views. */
  operation: 'INSERT' | 'UPDATE' | 'DELETE' | 'TRUNCATE' | null;
  source: 'HTTP' | 'JOB' | 'DB';
  app: string | null;
  actorType: ActivityActorType;
  actorId: string;
  actorName: string;
  firmId: number | null;
  sessionId: string | null;
  correlationId: string | null;
  subjectType: string | null;
  subjectId: string | null;
  subjectLabel: string | null;
  matter: ActivityMatter | null;
  httpMethod: string | null;
  httpRoute: string | null;
  changedFieldCount: number;
  changes: ActivityFieldChange[];
  /** The metadata the action shows to firms, e.g. { method: 'ACH' }; absent when none. */
  details?: Record<string, string | number | boolean>;
}

export interface ActivityEventsPage {
  resultSet: ActivityEvent[];
  totalCount?: number;
  failureCount?: number;
}

export interface ActivityActor {
  actorType: ActivityActorType;
  actorId: string;
  name: string;
}

export interface ActivityActorSummary extends ActivityActor {
  roleLabel: string;
  firstSeenAt: string | null;
  lastSeenAt: string | null;
  sessionCount: number;
  matters: ActivityMatter[];
}

export interface ActivityFilters {
  from: Date;
  to: Date;
  search?: string;
  actorTypes?: ActivityActorType[];
  actorId?: string;
  subjectTypes?: string[];
  caseId?: number;
  operation?: ActivityOperation;
  categories?: ActivityCategory[];
  failuresOnly?: boolean;
}

/** Query string for the audit endpoints; empty values are left out. */
const toParams = (filters: ActivityFilters) => ({
  from: filters.from.toISOString(),
  to: filters.to.toISOString(),
  search: filters.search?.trim() || undefined,
  actorTypes: filters.actorTypes?.length ? filters.actorTypes.join(',') : undefined,
  actorId: filters.actorId || undefined,
  subjectTypes: filters.subjectTypes?.length ? filters.subjectTypes.join(',') : undefined,
  caseId: filters.caseId || undefined,
  operation: filters.operation || undefined,
  categories: filters.categories?.length ? filters.categories.join(',') : undefined,
  failuresOnly: filters.failuresOnly ? 'true' : undefined,
});

export const getActivity = async (
  filters: ActivityFilters,
  page: { page: number; size: number; count?: boolean },
) => {
  return await api.get<ActivityEventsPage>(BASE_URL, {
    params: {
      ...toParams(filters),
      page: page.page,
      size: page.size,
      count: page.count ? 'true' : undefined,
    },
  });
};

export const getActivityActors = async (from: Date, to: Date, actorTypes?: ActivityActorType[]) => {
  return await api.get<{ resultSet: ActivityActor[] }>(`${BASE_URL}/actors`, {
    params: {
      from: from.toISOString(),
      to: to.toISOString(),
      actorTypes: actorTypes?.length ? actorTypes.join(',') : undefined,
    },
  });
};

export const getActivityActorSummary = async (actorType: ActivityActorType, actorId: string) => {
  return await api.get<ActivityActorSummary>(
    `${BASE_URL}/actors/${actorType}/${encodeURIComponent(actorId)}`,
  );
};

/** The filtered activity for an export (at most 5000 rows); the page writes the CSV itself. */
export const exportActivity = async (filters: ActivityFilters) => {
  return await api.get<{ resultSet: ActivityEvent[] }>(`${BASE_URL}/export`, {
    params: toParams(filters),
  });
};
